import { useMediaQuery } from "@mui/material"
import { MOTION } from "./theme"

export { MOTION }

/**
 * How far apart staggered siblings start, and how many of them get a delay
 * before the stagger stops accumulating.
 *
 * The cap is the important number. A 40ms step is pleasant across eight rows
 * and absurd across two hundred — the last row of a full invoice table would
 * wait eight seconds to appear, which is not an animation, it is an outage.
 * Past the cap everything shares the last slot's delay, so a long list still
 * reads as sweeping in but finishes in under half a second.
 */
export const STAGGER_STEP = 40
export const STAGGER_CAP = 8

/**
 * Does this person want things to stop moving?
 *
 * The CSS in `index.css` already neutralises every animation for them, so this
 * is only needed where the decision is in JavaScript rather than a stylesheet —
 * choosing not to run an interval, or handing MUI a transition duration of 0.
 */
export function useReducedMotion() {
  return useMediaQuery("(prefers-reduced-motion: reduce)", { noSsr: true })
}

/**
 * The app's entrance: eight pixels up and a fade.
 *
 * `index` staggers a list. Pass it from a map and the rows arrive in the order
 * they are read, which is the whole effect — a table that appears all at once
 * reads as a screenshot, and one that appears in sequence reads as a table
 * being filled in.
 */
export function rise(index = 0, { duration = MOTION.base, delay = 0 } = {}) {
  const step = Math.min(index, STAGGER_CAP) * STAGGER_STEP
  return {
    animation: `tw-rise ${duration}ms ${MOTION.easeOut} backwards`,
    animationDelay: `${delay + step}ms`,
  }
}

/** A fade with no travel, for something replacing itself in place. */
export function fade(duration = MOTION.base, delay = 0) {
  return {
    animation: `tw-fade ${duration}ms ${MOTION.ease} backwards`,
    animationDelay: `${delay}ms`,
  }
}

/**
 * The slow opacity beat on a live indicator.
 *
 * Guarded by a media query rather than the global reduce rule, because that rule
 * works by making animations finish instantly — which for an infinite pulse
 * leaves the element parked at whatever opacity frame one happens to be. Here it
 * is simply not applied.
 */
export const PULSE = {
  "@media (prefers-reduced-motion: no-preference)": {
    animation: `tw-pulse 2s ${MOTION.ease} infinite`,
  },
}

/**
 * A card that responds to the pointer.
 *
 * The border moves to brand green; the card does not lift. Shadow in this
 * product means "this floats above the page" and is reserved for menus,
 * popovers and dialogs — spending it on hover would leave nothing to say that a
 * dialog is modal.
 */
export const INTERACTIVE_CARD = {
  cursor: "pointer",
  transition: `border-color ${MOTION.fast}ms ${MOTION.ease}, background-color ${MOTION.fast}ms ${MOTION.ease}`,
  "&:hover": { borderColor: "primary.main", bgcolor: "action.hover" },
}
